import { LabWorkbookStatus } from '@resistapp/common/types';

const ORDERED_STATUSES: LabWorkbookStatus[] = [
  LabWorkbookStatus.DRAFT,
  LabWorkbookStatus.SAMPLE_SELECTION,
  LabWorkbookStatus.SUBMITTED,
  LabWorkbookStatus.MEASURED,
  LabWorkbookStatus.PRIMERS_PREPPED,
  LabWorkbookStatus.SAMPLES_PREPPED,
  LabWorkbookStatus.RUNNING,
  LabWorkbookStatus.COMPLETED,
  LabWorkbookStatus.LEGACY,
];

const STATUS_ORDER: Record<LabWorkbookStatus, number> = {
  [LabWorkbookStatus.DRAFT]: 0,
  [LabWorkbookStatus.SAMPLE_SELECTION]: 1,
  [LabWorkbookStatus.SUBMITTED]: 2,
  [LabWorkbookStatus.MEASURED]: 3,
  [LabWorkbookStatus.PRIMERS_PREPPED]: 4,
  [LabWorkbookStatus.SAMPLES_PREPPED]: 5,
  [LabWorkbookStatus.RUNNING]: 6,
  [LabWorkbookStatus.COMPLETED]: 7,
  [LabWorkbookStatus.LEGACY]: 8,
};

export function getStatusRank(status: LabWorkbookStatus): number {
  return STATUS_ORDER[status];
}

export function isStatusAtLeast(
  status: LabWorkbookStatus | null | undefined,
  target: LabWorkbookStatus,
): boolean {
  if (!status) {
    return false;
  }
  return getStatusRank(status) >= getStatusRank(target);
}

export type LabViewReadOnlyState = {
  workbook: boolean;
  geneTemplate: boolean;
  dnaExtraction: boolean;
  primerPrep: boolean;
  samplePrep: boolean;
  smartChipRun: boolean;
};

export function deriveReadOnlyState(status: LabWorkbookStatus | null | undefined): LabViewReadOnlyState {
  const workbookReadOnly = status === LabWorkbookStatus.LEGACY;
  return {
    workbook: workbookReadOnly,
    geneTemplate: workbookReadOnly || isStatusAtLeast(status, LabWorkbookStatus.SUBMITTED),
    dnaExtraction: workbookReadOnly || isStatusAtLeast(status, LabWorkbookStatus.MEASURED),
    primerPrep: workbookReadOnly || isStatusAtLeast(status, LabWorkbookStatus.PRIMERS_PREPPED),
    samplePrep: workbookReadOnly || isStatusAtLeast(status, LabWorkbookStatus.SAMPLES_PREPPED),
    smartChipRun: workbookReadOnly || isStatusAtLeast(status, LabWorkbookStatus.COMPLETED),
  };
}

export function isGeneTemplateReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).geneTemplate;
}

export function isPrimerPrepReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).primerPrep;
}

export function isDnaExtractionReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).dnaExtraction;
}

export function isSamplePrepReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).samplePrep;
}

export function isSmartChipRunReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).smartChipRun;
}

export function isWorkbookReadOnly(status: LabWorkbookStatus | null | undefined): boolean {
  return deriveReadOnlyState(status).workbook;
}

export const LAB_STATUS_SEQUENCE = ORDERED_STATUSES;
